import "./journal.page.css";

import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import CenteredContainer from "../../components/layout/CenteredContainer/CenteredContainer";
import PageLoadState from "../../components/layout/PageLoadState/PageLoadState";
import SaveToast from "../../components/layout/SaveToast/SaveToast";
import TaskBoard from "./TaskBoard";
import { fetchGet } from "../../clients/response";
import { SERVICE_URL } from "../../clients/app.const";
import { mapJsonToCharacter } from "../../models/Character";
import { systemLabelKey } from "../../catalogs/characterSystemCatalog";
import { useCharacterAutoSave } from "../../hooks/useCharacterAutoSave";
import { useCharacterAvatarSource } from "../../hooks/useCharacterAvatarSource";

const APPEARANCE_FIELDS = [
  { key: "gender", labelKey: "journal.gender", type: "text" },
  { key: "eyes", labelKey: "journal.eyes", type: "text" },
  { key: "hair", labelKey: "journal.hair", type: "text" },
  { key: "skin", labelKey: "journal.skin", type: "text" },
  { key: "height", labelKey: "journal.height", type: "number" },
  { key: "weight", labelKey: "journal.weight", type: "number" },
];

const JournalPage = () => {
  const { t } = useTranslation();
  const { tokenValue, user } = useSelector((state) => state.auth);
  const selectedCharacterId = useSelector(
    (state) => state.character.selectedCharacterId
  );
  const [character, setCharacter] = useState();
  const [loadState, setLoadState] = useState("loading");
  const [reloadKey, setReloadKey] = useState(0);
  const { dismissSaveMessage, isSaveError, retrySave, saveMessage } =
    useCharacterAutoSave(character, tokenValue);
  const avatarSource = useCharacterAvatarSource(character?.avatarUrl, tokenValue);

  useEffect(() => {
    let cancelled = false;

    const loadCharacter = async () => {
      if (!selectedCharacterId) {
        setCharacter(undefined);
        setLoadState("empty");
        return;
      }

      setLoadState("loading");
      const response = await fetchGet(
        `${SERVICE_URL}/characters/${selectedCharacterId}`,
        {},
        tokenValue
      );

      if (cancelled) return;
      if (response.isError || !response.data) {
        setCharacter(undefined);
        setLoadState("error");
        return;
      }

      try {
        setCharacter(mapJsonToCharacter(response.data));
        setLoadState("ready");
      } catch (error) {
        console.error("Failed to process character journal:", error);
        setCharacter(undefined);
        setLoadState("error");
      }
    };

    loadCharacter();
    return () => {
      cancelled = true;
    };
  }, [selectedCharacterId, tokenValue, user, reloadKey]);

  const updateField = (field, value) => {
    setCharacter((current) => ({ ...current, [field]: value }));
  };

  const updateAppearance = (field, value) => {
    setCharacter((current) => ({
      ...current,
      appearance: {
        ...current.appearance,
        [field]: value,
      },
    }));
  };

  const classSummary =
    character?.characterClass
      ?.filter((characterClass) => characterClass.id)
      .map((characterClass) => `${t(systemLabelKey("classes", characterClass.id))}${characterClass.classLevel ? ` ${characterClass.classLevel}` : ""}`)
      .join(" / ") || t("characters.noClass");

  return (
    <CenteredContainer>
      <PageLoadState
        state={loadState}
        message={
          loadState === "loading"
            ? t("journal.loading")
            : loadState === "empty"
              ? t("journal.selectCharacter")
              : t("journal.loadError")
        }
        onRetry={() => setReloadKey((key) => key + 1)}
      />

      {loadState === "ready" && character && (
        <main className="journal-container">
          <SaveToast
            isError={isSaveError}
            message={saveMessage}
            onDismiss={dismissSaveMessage}
            onRetry={retrySave}
          />

          <section className="journal-hero">
            <div className="journal-avatar">
              {avatarSource ? (
                <img alt="" src={avatarSource} />
              ) : (
                <span>{character.characterName?.charAt(0) || "?"}</span>
              )}
            </div>
            <div className="journal-identity">
              <p>{t("journal.title")}</p>
              <h1>{character.characterName || t("characters.unnamed")}</h1>
              <div className="journal-summary">
                <span>{character.race?.id ? t(systemLabelKey("races", character.race.id)) : t("characters.noRace")}</span>
                <span>{classSummary}</span>
                <span>{t("characters.level", { level: character.level?.level || 0 })}</span>
                <span>{character.alignmentId ? t(systemLabelKey("alignments", character.alignmentId)) : t("characters.noAlignment")}</span>
                <span>{character.background || t("characters.noBackground")}</span>
              </div>
            </div>
          </section>

          <div className="journal-grid">
            <section className="journal-section appearance-section">
              <div className="journal-section-heading">
                <div>
                  <h2>{t("journal.appearance")}</h2>
                  <p>{t("journal.appearanceHint")}</p>
                </div>
              </div>
              <div className="appearance-grid">
                {APPEARANCE_FIELDS.map((field) => (
                  <label key={field.key}>
                    {t(field.labelKey)}
                    <input
                      min={field.type === "number" ? 0 : undefined}
                      type={field.type}
                      value={character.appearance?.[field.key] ?? ""}
                      onChange={(event) =>
                        updateAppearance(field.key, event.target.value)
                      }
                    />
                  </label>
                ))}
              </div>
            </section>

            <section className="journal-section story-section">
              <div className="journal-section-heading">
                <div>
                  <h2>{t("journal.story")}</h2>
                  <p>{t("journal.storyHint")}</p>
                </div>
              </div>
              <label>
                {t("journal.background")}
                <input
                  value={character.background ?? ""}
                  onChange={(event) =>
                    updateField("background", event.target.value)
                  }
                />
              </label>
              <label>
                {t("journal.deity")}
                <input
                  value={character.god ?? ""}
                  onChange={(event) => updateField("god", event.target.value)}
                />
              </label>
              <label className="journal-wide-field">
                {t("journal.backstory")}
                <textarea
                  rows="8"
                  value={character.backstory ?? ""}
                  onChange={(event) =>
                    updateField("backstory", event.target.value)
                  }
                />
              </label>
            </section>

            <section className="journal-section journal-notes-section">
              <div className="journal-section-heading">
                <div>
                  <h2>{t("journal.affiliations")}</h2>
                  <p>{t("journal.affiliationsHint")}</p>
                </div>
              </div>
              <textarea
                aria-label={t("journal.affiliations")}
                rows="8"
                value={character.affiliations ?? ""}
                onChange={(event) =>
                  updateField("affiliations", event.target.value)
                }
              />
            </section>

            <section className="journal-section journal-notes-section">
              <div className="journal-section-heading">
                <div>
                  <h2>{t("journal.achievements")}</h2>
                  <p>{t("journal.achievementsHint")}</p>
                </div>
              </div>
              <textarea
                aria-label={t("journal.achievements")}
                rows="8"
                value={character.achievements ?? ""}
                onChange={(event) =>
                  updateField("achievements", event.target.value)
                }
              />
            </section>
          </div>

          <TaskBoard
            tasks={character.tasks}
            onChange={(tasks) => updateField("tasks", tasks)}
          />
        </main>
      )}
    </CenteredContainer>
  );
};

export default JournalPage;
